/**
 * Custom Product Options (Addons) — Line-Item Approach
 * -----------------------------------------------------
 * • Renders addon total on the product page (display price = base + addons)
 * • Intercepts the add-to-cart form submission
 * • Adds the main product + any selected addon products as separate line items
 *   using Shopify's /cart/add.js multi-item endpoint
 * • Links addons to the parent via a hidden `_parent` line-item property
 *
 * Shopify prices are in **cents**. The metafield stores prices in whole
 * dollars so we multiply by 100 when working with Shopify helpers.
 */

(function () {
  'use strict';

  /* ---- helpers ---- */
  function formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---- main ---- */
  function initCustomOptions(root) {
    var scope = root || document;
    var wrapper = scope.matches && scope.matches('[data-custom-options]')
      ? scope
      : scope.querySelector && scope.querySelector('[data-custom-options]');
    if (!wrapper) return;

    // Prevent double initialization
    if (wrapper.hasAttribute('data-initialized')) return;
    wrapper.setAttribute('data-initialized', 'true');

    /* ---- Tab switching functionality ---- */
    var tabBtns = wrapper.querySelectorAll('[data-tab]');
    var tabContents = wrapper.querySelectorAll('[data-tab-content]');
    var hasTabsEnabled = tabBtns.length > 0;
    // When every group has disableCustomization: true the Liquid snippet renders
    // only the prefabricated content with no tab-nav. Detect that so default-mode
    // decisions below land on 'prefabricated' instead of the usual 'customize'.
    var prefabOnly = !hasTabsEnabled && !!wrapper.querySelector('[data-tab-content="prefabricated"]');
    var customizeTabInitialized = false; // Track if customize tab has been set up

    function switchTab(tabName) {
      if (!hasTabsEnabled) return; // No tabs to switch

      // Update button states
      tabBtns.forEach(function (btn) {
        var btnTab = btn.getAttribute('data-tab');
        if (btnTab === tabName) {
          btn.classList.add('is-active');
        } else {
          btn.classList.remove('is-active');
        }
      });

      // Update content visibility
      tabContents.forEach(function (content) {
        var contentTab = content.getAttribute('data-tab-content');
        if (contentTab === tabName) {
          content.classList.add('is-active');
        } else {
          content.classList.remove('is-active');
        }
      });

      // Update pricing for the active tab
      if (tabName === 'prefabricated') {
        updatePrebuiltPricing();
      } else {
        if (!customizeTabInitialized) {
          preselectPrebuiltOptions();
          customizeTabInitialized = true;
        } else {
          updateCustomizeVisibility();
          updateDependencies();
        }
        update();
      }
    }

    /**
     * When the Customize tab is activated, preselect the prebuilt variant
     * in each dropdown and show only groups that are required/conditionally
     * required based on the prebuilt selections.
     */
    function preselectPrebuiltOptions() {
      var allGroups = wrapper.querySelectorAll('[data-option-group]');

      // Step 1: Preselect the prebuilt variant in each dropdown
      allGroups.forEach(function (groupEl) {
        var prebuiltVariant = groupEl.getAttribute('data-prebuilt-variant');
        if (!prebuiltVariant) return;

        var sel = groupEl.querySelector('[data-custom-option-select]');
        if (!sel) return;

        // Find and select the prebuilt option
        for (var i = 0; i < sel.options.length; i++) {
          var vid = sel.options[i].getAttribute('data-variant-id');
          if (vid && String(vid) === String(prebuiltVariant)) {
            sel.selectedIndex = i;
            break;
          }
        }

        // Sync the custom dropdown UI
        var customDD = groupEl.querySelector('.custom-dropdown');
        if (customDD) {
          var triggerText = customDD.querySelector('.custom-dropdown__text');
          var selectedOpt = sel.options[sel.selectedIndex];
          if (triggerText && selectedOpt) {
            triggerText.textContent = selectedOpt.textContent;
          }
          customDD.querySelectorAll('.custom-dropdown__item').forEach(function (li) {
            li.classList.remove('is-selected');
            if (parseInt(li.getAttribute('data-index'), 10) === sel.selectedIndex) {
              li.classList.add('is-selected');
            }
          });
        }
      });

      // Step 2: Re-evaluate standard dependencies after preselections
      updatingDeps = true;
      updateDependencies();
      updatingDeps = false;

      // Step 3: Determine visibility based on prebuilt selections
      updateCustomizeVisibility();
    }

    // Add click handlers to tab buttons (only if tabs exist)
    if (hasTabsEnabled) {
      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var tabName = btn.getAttribute('data-tab');
          switchTab(tabName);
        });
      });
    }
    /* ---- Addon info ⓘ buttons → fetch page content & open tooltip ---- */
    var infoCache = {};
    wrapper.querySelectorAll('[data-addon-info-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var context = btn.getAttribute('data-addon-info-trigger');
        var pageUrl = btn.getAttribute('data-info-url');
        if (!pageUrl) return;

        function openTooltip(html) {
          document.dispatchEvent(new CustomEvent('tooltip:open', {
            detail: { context: context, content: html },
            bubbles: true
          }));
        }

        if (infoCache[pageUrl]) {
          openTooltip(infoCache[pageUrl]);
          return;
        }

        fetch(pageUrl)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var el = doc.querySelector('.page-content .rte') || doc.querySelector('#MainContent .rte') || doc.querySelector('#MainContent');
            var result = el ? el.innerHTML : '';
            infoCache[pageUrl] = result;
            openTooltip(result);
          });
      });
    });

    var formId = wrapper.getAttribute('data-form-id');
    var form = document.getElementById(formId);
    var selects = wrapper.querySelectorAll('[data-custom-option-select]');
    var totalWrap = wrapper.querySelector('[data-addons-total-wrap]');
    var totalEl = wrapper.querySelector('[data-addons-total]');
    var productTitle = wrapper.getAttribute('data-product-title') || 'Product';

    // Price element used by the theme to show the variant price
    var section = wrapper.closest('[data-section-id]') || document;
    var priceEl = section.querySelector('[data-product-price]');

    // Keep track of the base variant price (in cents) so we can add on top
    var basePrice = 0;

    /* ---- Custom dropdown builder ---- */
    function buildCustomDropdown(nativeSelect) {
      var container = document.createElement('div');
      container.className = 'custom-dropdown';

      // Trigger button
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'custom-dropdown__trigger';
      var selectedOpt = nativeSelect.options[nativeSelect.selectedIndex];
      trigger.innerHTML = '<span class="custom-dropdown__text">' +
        (selectedOpt ? selectedOpt.textContent : '') +
        '</span>' +
        '<svg class="custom-dropdown__arrow" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>';

      // Options list
      var list = document.createElement('ul');
      list.className = 'custom-dropdown__list';

      Array.prototype.forEach.call(nativeSelect.options, function (opt, idx) {
        var li = document.createElement('li');
        li.className = 'custom-dropdown__item';
        li.setAttribute('data-index', idx);
        li.textContent = opt.textContent;
        if (opt.disabled) li.classList.add('is-disabled');
        if (nativeSelect.selectedIndex === idx) li.classList.add('is-selected');

        li.addEventListener('click', function (e) {
          e.stopPropagation();
          if (opt.disabled) return;
          nativeSelect.selectedIndex = idx;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          // Update trigger text
          trigger.querySelector('.custom-dropdown__text').textContent = opt.textContent;
          // Update selected state
          list.querySelectorAll('.custom-dropdown__item').forEach(function (item) {
            item.classList.remove('is-selected');
          });
          li.classList.add('is-selected');
          // Close
          container.classList.remove('is-open');
        });

        list.appendChild(li);
      });

      // Toggle open/close
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        // Close all other dropdowns first
        document.querySelectorAll('.custom-dropdown.is-open').forEach(function (dd) {
          if (dd !== container) dd.classList.remove('is-open');
        });
        container.classList.toggle('is-open');
      });

      container.appendChild(trigger);
      container.appendChild(list);
      nativeSelect.parentNode.insertBefore(container, nativeSelect.nextSibling);
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', function () {
      document.querySelectorAll('.custom-dropdown.is-open').forEach(function (dd) {
        dd.classList.remove('is-open');
      });
    });

    // Build custom dropdowns for all addon selects
    selects.forEach(function (sel) {
      buildCustomDropdown(sel);
    });

    function getBasePrice() {
      // If we already have a cached base price from a variant change event, use it
      if (basePrice > 0) return basePrice;

      // Try the current variant price passed from Liquid (most reliable for initial load)
      var currentVariantPrice = parseInt(wrapper.getAttribute('data-current-variant-price'), 10);
      if (currentVariantPrice > 0) return currentVariantPrice;

      // Try the selected variant from the form
      var allVariants = section.querySelector('[data-variant-json]');
      if (allVariants) {
        try {
          var variants = JSON.parse(allVariants.textContent);
          var selectedId = form ? form.querySelector('[name="id"]').value : null;
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === String(selectedId)) {
              return variants[i].price;
            }
          }
        } catch (e) { }
      }

      // Try the current variant JSON element
      var variantJson = section.querySelector('[data-current-variant-json]');
      if (variantJson) {
        try {
          var v = JSON.parse(variantJson.textContent);
          return v.price;
        } catch (e) { }
      }
      return 0;
    }

    function getAddonTotal() {
      var total = 0;
      selects.forEach(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        if (opt) {
          var price = parseInt(opt.getAttribute('data-addon-price'), 10) || 0;
          total += price;
        }
      });
      return total; // in dollars
    }

    /* ---- Update prebuilt pricing display ---- */
    function updatePrebuiltPricing() {
      basePrice = getBasePrice();
      var prebuiltTotal = 0;

      // Calculate prebuilt total
      var prebuiltItems = wrapper.querySelectorAll('[data-prebuilt-option]');
      prebuiltItems.forEach(function (item) {
        var price = parseFloat(item.getAttribute('data-prebuilt-price')) || 0;
        prebuiltTotal += price;
      });

      var prebuiltCents = prebuiltTotal * 100;

      // Update prebuilt base price display
      var prebuiltBasePriceWrap = wrapper.querySelector('[data-prebuilt-base-price-wrap]');
      var prebuiltBasePriceEl = wrapper.querySelector('[data-prebuilt-base-price]');
      if (prebuiltBasePriceWrap && prebuiltBasePriceEl) {
        prebuiltBasePriceWrap.style.display = '';
        prebuiltBasePriceEl.innerHTML = formatMoney(basePrice);
      }

      // Update prebuilt grand total
      var prebuiltGrandTotalWrap = wrapper.querySelector('[data-prebuilt-grand-total-wrap]');
      var prebuiltGrandTotalEl = wrapper.querySelector('[data-prebuilt-grand-total]');
      if (prebuiltGrandTotalWrap && prebuiltGrandTotalEl) {
        prebuiltGrandTotalWrap.style.display = '';
        prebuiltGrandTotalEl.innerHTML = formatMoney(basePrice + prebuiltCents);
      }
    }

    /**
     * Get the currently active tab name
     */
    function getActiveTab() {
      if (!hasTabsEnabled) return prefabOnly ? 'prefabricated' : 'customize';
      var activeBtn = wrapper.querySelector('.product-custom-options__tab-btn.is-active');
      return activeBtn ? activeBtn.getAttribute('data-tab') : 'prefabricated';
    }

    /**
     * Collect prebuilt addon variants in display order from prebuilt lines
     * Returns an array of { id: variantId, quantity: 1, properties: {...} }
     */
    function getSelectedVariantId() {
      // The product form's id input is updated synchronously by the theme on
      // variant change — the reliable source at Add to Cart time.
      var form = document.getElementById(formId);
      if (form) {
        var idInput = form.querySelector('[name="id"]');
        if (idInput && idInput.value) return String(idInput.value);
      }
      return wrapper.getAttribute('data-current-variant-id') || '';
    }

    // The selected variant's prebuilt components ([{id, group, title, price}]),
    // read synchronously from the embedded per-variant map. Race-free: it does not
    // depend on the async section reload having re-rendered the breakdown DOM.
    // Returns null when there is no map (non-per-variant products) so callers fall
    // back to reading the rendered breakdown lines.
    function getSelectedPrebuiltEntries() {
      var mapEl = wrapper.querySelector('script[data-prebuilt-map]');
      var vid = getSelectedVariantId();
      if (!mapEl || !vid) return null;
      try {
        var map = JSON.parse(mapEl.textContent);
        return map[vid] || null;
      } catch (e) { return null; }
    }

    function getPrebuiltAddonItems(parentKey) {
      var items = [];

      var entry = getSelectedPrebuiltEntries();
      if (entry) {
        entry.forEach(function (g) {
          if (!g || !g.id) return;
          items.push({
            id: parseInt(g.id, 10),
            quantity: 1,
            properties: {
              '_parent': parentKey,
              '_addon_group': g.group,
              '_addon_for': productTitle
            }
          });
        });
        return items;
      }

      // Fallback: use the prebuilt lines in the order they are displayed
      var prebuiltLines = wrapper.querySelectorAll('[data-prebuilt-option][data-prebuilt-variant-id]');

      prebuiltLines.forEach(function (lineEl) {
        var variantId = lineEl.getAttribute('data-prebuilt-variant-id');
        var optionName = lineEl.getAttribute('data-prebuilt-option');

        if (!variantId || !optionName) return;

        // Convert handleized option name back to readable format
        // Find the corresponding option group to get the proper name
        var groupEl = wrapper.querySelector('[data-option-group="' + optionName + '"]');
        var displayName = groupEl ? groupEl.getAttribute('data-option-name') : optionName;

        items.push({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_parent': parentKey,
            '_addon_group': displayName || optionName,
            '_addon_for': productTitle
          }
        });
      });

      return items;
    }

    /**
     * Collect the selected addon variants from custom dropdowns
     * Returns an array of { id: variantId, quantity: 1, properties: {...} }
     */
    function getCustomAddonItems(parentKey) {
      var items = [];
      selects.forEach(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        if (!opt) return;

        var variantId = opt.getAttribute('data-variant-id');
        if (!variantId) return; // "No addon" or placeholder option

        var optionName = sel.getAttribute('data-option-name');
        var price = parseInt(opt.getAttribute('data-addon-price'), 10) || 0;

        items.push({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_parent': parentKey,
            '_addon_group': optionName,
            '_addon_for': productTitle
          }
        });
      });
      return items;
    }

    /**
     * Get addon items based on active tab
     */
    function getAddonItems(parentKey) {
      var activeTab = getActiveTab();
      if (activeTab === 'prefabricated') {
        return getPrebuiltAddonItems(parentKey);
      } else {
        return getCustomAddonItems(parentKey);
      }
    }

    /**
     * Build the main product's line-item properties based on active tab.
     * Includes human-readable addon descriptions and a unique key
     * to link addon items back to this parent.
     */
    function getMainProductProperties() {
      var props = {};
      var hasAddons = false;
      var activeTab = getActiveTab();

      if (activeTab === 'prefabricated') {
        // Prefer reading from the customize-side option groups when they're
        // present — they carry the authoritative display name and the full
        // variant lookup via <select>. When the customize block is absent
        // (every group has disableCustomization: true, so Liquid skips it),
        // fall back to the prefabricated breakdown lines, which carry enough
        // information on their own data attributes.
        var groupEls = wrapper.querySelectorAll('[data-option-group]');
        if (groupEls.length > 0) {
          groupEls.forEach(function (groupEl) {
            var prebuiltVariant = groupEl.getAttribute('data-prebuilt-variant');
            if (!prebuiltVariant) return;

            var optionName = groupEl.getAttribute('data-option-display-name') || groupEl.getAttribute('data-option-name') || 'Option';
            var sel = groupEl.querySelector('[data-custom-option-select]');
            if (!sel) return;

            // Find the prebuilt variant option
            var prebuiltOption = null;
            for (var i = 0; i < sel.options.length; i++) {
              var opt = sel.options[i];
              var variantId = opt.getAttribute('data-variant-id');
              if (variantId === prebuiltVariant) {
                prebuiltOption = opt;
                break;
              }
            }

            if (!prebuiltOption) return;

            var variantId = prebuiltOption.getAttribute('data-variant-id');
            var price = parseInt(prebuiltOption.getAttribute('data-addon-price'), 10) || 0;

            // Human-readable property visible in cart/checkout/orders
            if (price > 0) {
              props[optionName] = prebuiltOption.textContent + ' (+' + formatMoney(price * 100) + ')';
            } else if (prebuiltOption.textContent) {
              props[optionName] = prebuiltOption.textContent;
            }

            // Mark as having addons if we have a variant ID
            if (variantId) {
              hasAddons = true;
            }
          });
        } else {
          // Prefab-only: prefer the per-variant map so the cart properties match
          // the selected variant synchronously (race-free), then fall back to the
          // rendered breakdown lines for products without a map.
          var prebuiltEntries = getSelectedPrebuiltEntries();
          if (prebuiltEntries) {
            prebuiltEntries.forEach(function (g) {
              if (!g || !g.id) return;
              var cents = parseInt(g.price, 10) || 0;
              var title = g.title || g.group;
              props[g.group] = cents > 0 ? title + ' (+' + formatMoney(cents) + ')' : title;
              hasAddons = true;
            });
          } else {
          wrapper.querySelectorAll('[data-prebuilt-option][data-prebuilt-variant-id]').forEach(function (lineEl) {
            var variantId = lineEl.getAttribute('data-prebuilt-variant-id');
            if (!variantId) return;

            // Rehydrate the option name from its handleized form on the line.
            var slug = (lineEl.getAttribute('data-prebuilt-option') || '').toString();
            var optionName = slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : 'Option';
            var titleEl = lineEl.querySelector('.product-custom-options__total-label');
            var title = titleEl ? titleEl.textContent.trim() : '';
            var priceDollars = parseFloat(lineEl.getAttribute('data-prebuilt-price')) || 0;

            if (title) {
              props[optionName] = priceDollars > 0
                ? title + ' (+' + formatMoney(priceDollars * 100) + ')'
                : title;
            }
            hasAddons = true;
          });
          }
        }
      } else {
        // For customize tab, show selected dropdown options
        selects.forEach(function (sel) {
          var opt = sel.options[sel.selectedIndex];
          if (!opt || !sel.value) return;

          var groupEl = sel.closest('[data-option-group]');
          var optionName = (groupEl && groupEl.getAttribute('data-option-display-name')) || sel.getAttribute('data-option-name');
          var variantId = opt.getAttribute('data-variant-id');
          var price = parseInt(opt.getAttribute('data-addon-price'), 10) || 0;

          // Human-readable property visible in cart/checkout/orders
          if (price > 0) {
            props[optionName] = sel.value + ' (+' + formatMoney(price * 100) + ')';
          } else if (sel.value) {
            props[optionName] = sel.value;
          }

          // Mark as having addons if ANY select has a variant ID chosen
          if (variantId) {
            hasAddons = true;
          }
        });
      }

      // Unique key linking addons → parent (hidden, starts with _)
      if (hasAddons) {
        props['_addon_key'] = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      }

      return props;
    }

    function update() {
      basePrice = getBasePrice();
      var addonDollars = getAddonTotal();
      var addonCents = addonDollars * 100;

      // Update base price display
      var basePriceWrap = wrapper.querySelector('[data-base-price-wrap]');
      var basePriceEl = wrapper.querySelector('[data-base-price]');
      if (basePriceWrap && basePriceEl) {
        basePriceWrap.style.display = '';
        basePriceEl.innerHTML = formatMoney(basePrice);
      }

      // Build itemized addon lines
      var addonLinesContainer = wrapper.querySelector('[data-addon-lines]');
      if (addonLinesContainer) {
        addonLinesContainer.innerHTML = '';
        selects.forEach(function (sel) {
          var opt = sel.options[sel.selectedIndex];
          if (!opt) return;
          var variantId = opt.getAttribute('data-variant-id');
          if (!variantId) return;
          var price = parseInt(opt.getAttribute('data-addon-price'), 10) || 0;
          var priceCents = price * 100;
          var groupEl = sel.closest('[data-option-group]');
          var name = (groupEl && groupEl.getAttribute('data-option-display-name')) || sel.getAttribute('data-option-name') || '';
          var title = opt.value || opt.textContent.trim();

          var line = document.createElement('div');
          line.className = 'product-custom-options__total product-custom-options__addon-line';
          line.innerHTML = '<span class="product-custom-options__total-label">' +
            name + ': ' + title + '</span>' +
            '<span class="product-custom-options__total-value">' +
            (priceCents > 0 ? '+' + formatMoney(priceCents) : formatMoney(0)) +
            '</span>';
          addonLinesContainer.appendChild(line);
        });
      }

      // Update grand total (base + addons)
      var grandTotalWrap = wrapper.querySelector('[data-grand-total-wrap]');
      var grandTotalEl = wrapper.querySelector('[data-grand-total]');
      if (grandTotalWrap && grandTotalEl) {
        grandTotalWrap.style.display = '';
        grandTotalEl.innerHTML = formatMoney(basePrice + addonCents);
      }

      // Shop Pay Installments widget reads the base variant price only.
      // When addons push the total above the variant price, hide the widget
      // and show a "see options at checkout" line so we don't mislead the shopper.
      var paymentTermsRoot = section.querySelector('[data-payment-terms]');
      if (paymentTermsRoot) {
        var widgetEl = paymentTermsRoot.querySelector('[data-payment-terms-widget]');
        var fallbackEl = paymentTermsRoot.querySelector('[data-payment-terms-fallback]');
        var showFallback = addonCents > 0;
        if (widgetEl) widgetEl.hidden = showFallback;
        if (fallbackEl) fallbackEl.hidden = !showFallback;
      }
    }

    /**
     * Intercept add-to-cart form submission.
     * We add the main product first, then addon variants.
     */
    function interceptAddToCart(evt) {
      // Always intercept if there are required option groups (even if nothing is selected yet)
      var hasRequiredGroups = wrapper.querySelector('[data-option-required="true"]') !== null;
      var activeTab = getActiveTab();
      var addonItems = getAddonItems('');
      if (addonItems.length === 0 && !hasRequiredGroups) return; // let the theme handle normally

      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();

      // Re-evaluate dependencies before validation (only needed for customize tab)
      if (activeTab === 'customize') {
        updateDependencies();
      }

      // Validate conditionally-required groups based on active tab
      var missingRequired = [];

      // Only validate selections for customize tab
      if (activeTab === 'customize') {
        wrapper.querySelectorAll('[data-option-group]').forEach(function (groupEl) {
          var isRequired = groupEl.getAttribute('data-option-required') === 'true';
          if (!isRequired) return;
          var isRequired = groupEl.getAttribute('data-option-required') === 'true';
          if (!isRequired) return;

          var sel = groupEl.querySelector('[data-custom-option-select]');
          if (!sel) return;
          var opt = sel.options[sel.selectedIndex];
          var vid = opt ? opt.getAttribute('data-variant-id') : '';
          if (!vid) {
            var groupEl2 = sel.closest('[data-option-group]');
            var name = (groupEl2 && groupEl2.getAttribute('data-option-display-name')) || sel.getAttribute('data-option-name') || 'Option';
            missingRequired.push(name);
          }
        });
      }
      // For prebuilt tab, no validation needed since options are pre-configured

      if (missingRequired.length > 0) {
        var errors = form.querySelector('.errors');
        if (!errors) {
          errors = document.createElement('div');
          errors.className = 'errors';
          form.prepend(errors);
        }
        errors.textContent = 'Please select: ' + missingRequired.join(', ');
        return;
      }

      var addBtn = form.querySelector('[data-add-to-cart]') || form.querySelector('.add-to-cart');
      if (addBtn) addBtn.classList.add('btn--loading');

      var variantId = form.querySelector('[name="id"]').value;
      var quantity = 1;
      var qtyInput = form.querySelector('[name="quantity"]');
      if (qtyInput) quantity = parseInt(qtyInput.value, 10) || 1;

      // Build main product properties with addon descriptions
      var mainProps = getMainProductProperties();
      var addonKey = mainProps['_addon_key'] || '';

      // Build the items array for /cart/add.js
      // Addon items go first so the main product (added last) appears
      // at the top of the cart (Shopify shows newest items first).
      var items = [];

      // Add addon items first, linked to parent via _parent = addonKey
      // Reverse the order because Shopify shows newest items first
      var addonItemsFinal = getAddonItems(addonKey);
      addonItemsFinal.slice().reverse().forEach(function (item) {
        item.quantity = quantity; // match parent quantity
        items.push(item);
      });

      // Main product goes last so it appears first in the cart
      items.push({
        id: parseInt(variantId, 10),
        quantity: quantity,
        properties: mainProps
      });

      fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (addBtn) addBtn.classList.remove('btn--loading');

          if (data.status === 422 || data.message) {
            // Error — show it
            var errors = form.querySelector('.errors');
            if (!errors) {
              errors = document.createElement('div');
              errors.className = 'errors';
              form.prepend(errors);
            }
            errors.textContent = data.description || data.message || 'Could not add to cart';
            return;
          }

          // Success — trigger the same events the theme expects
          var firstItem = Array.isArray(data.items) ? data.items[0] : data;

          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: firstItem, addToCartBtn: addBtn },
            bubbles: true
          }));

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: firstItem, addToCartBtn: addBtn }
          }));

          // If theme uses page cart, redirect
          if (typeof theme !== 'undefined' && theme.settings && theme.settings.cartType === 'page') {
            window.location = theme.routes.cartPage;
          }
        })
        .catch(function (err) {
          if (addBtn) addBtn.classList.remove('btn--loading');
          console.error('Custom options add-to-cart error:', err);
        });
    }

    // Intercept form submission — use capture phase to run before theme's handler
    if (form) {
      form.addEventListener('submit', interceptAddToCart, true);
    }

    // Guard to prevent recursive change handler calls during updateDependencies
    var updatingDeps = false;

    // Listen for addon dropdown changes
    selects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        if (updatingDeps) return; // skip recursive triggers from dependency resets
        updatingDeps = true;
        updateDependencies();
        updatingDeps = false;
        updateCustomizeVisibility();
        update();
      });
    });

    /**
     * Re-evaluate which groups should be visible in the Customize tab
     * based on current selections (for requiredIf dependencies).
     *
     * A group is shown if ANY of these is true:
     *   1. It has a prebuilt variant (it's a base option)
     *   2. It is always-required (option.required = true)
     *   3. Its requiredIf rule is currently triggered by a selected variant
     *
     * A group is hidden if notAvailableIf triggers (handled separately by
     * updateDependencies — we just skip those here).
     */
    function updateCustomizeVisibility() {
      if (getActiveTab() !== 'customize') return;

      // All groups are visible on the customize tab.
      // Only notAvailableIf (handled by updateDependencies) hides groups.
      // requiredIf only toggles the required attribute, not visibility.
      var allGroups = wrapper.querySelectorAll('[data-option-group]');
      allGroups.forEach(function (groupEl) {
        if (groupEl.classList.contains('is-not-available')) return;
        groupEl.classList.remove('is-prebuilt-hidden');
      });
    }

    /* ---- Parse full addon config from embedded JSON ---- */
    var addonConfig = [];
    var configScript = wrapper.querySelector('script[data-addon-config]');
    if (configScript) {
      try { addonConfig = JSON.parse(configScript.textContent); } catch (e) { }
    }

    // Build lookup: option name → notAvailableIf rules
    var notAvailableIfMap = {};
    addonConfig.forEach(function (opt) {
      if (opt.notAvailableIf && opt.notAvailableIf.length > 0) {
        notAvailableIfMap[opt.name] = opt.notAvailableIf;
      }
    });

    /**
     * Evaluate requiredIf and notAvailableIf dependencies.
     *
     * requiredIf:     make a group required when a trigger variant is selected.
     * notAvailableIf: hide + disable a group when a trigger variant is selected.
     */
    function updateDependencies() {
      var allGroups = wrapper.querySelectorAll('[data-option-group]');

      /* ---- notAvailableIf ---- */
      allGroups.forEach(function (groupEl) {
        var optionName = groupEl.getAttribute('data-option-name');
        var naRules = notAvailableIfMap[optionName];
        if (!naRules || naRules.length === 0) return;

        var sel = groupEl.querySelector('[data-custom-option-select]');
        if (!sel) return;

        var shouldHide = false;

        naRules.forEach(function (rule) {
          if (shouldHide) return; // already triggered
          var triggerSel = wrapper.querySelector(
            '[data-custom-option-select][data-option-name="' + rule.group + '"]'
          );
          if (!triggerSel) return;

          var triggerOpt = triggerSel.options[triggerSel.selectedIndex];
          if (!triggerOpt) return;
          var triggerVariantId = triggerOpt.getAttribute('data-variant-id');
          if (!triggerVariantId) return;

          // Compare as strings — JSON may contain numeric IDs
          if (rule.variantIds && rule.variantIds.map(String).indexOf(String(triggerVariantId)) !== -1) {
            shouldHide = true;
          }
        });

        var wasHidden = groupEl.classList.contains('is-not-available');
        var naDisplay = wrapper.getAttribute('data-not-available-display') || 'hide';

        if (shouldHide) {
          groupEl.classList.add('is-not-available');
          sel.disabled = true;
          sel.removeAttribute('required');
          groupEl.setAttribute('data-option-required', 'false');

          if (naDisplay === 'disable') {
            groupEl.classList.add('is-not-available--disabled');
            groupEl.classList.remove('is-not-available--hidden');
          } else {
            groupEl.classList.add('is-not-available--hidden');
            groupEl.classList.remove('is-not-available--disabled');
          }

          // Reset to the "no selection" option when hiding
          if (!wasHidden) {
            // Find the first option with an empty variant-id (placeholder)
            for (var k = 0; k < sel.options.length; k++) {
              var vid = sel.options[k].getAttribute('data-variant-id');
              if (!vid || vid === '') {
                sel.selectedIndex = k;
                break;
              }
            }
            sel.dispatchEvent(new Event('change', { bubbles: true }));

            // Sync the custom dropdown trigger text
            var customDD = groupEl.querySelector('.custom-dropdown');
            if (customDD) {
              var triggerText = customDD.querySelector('.custom-dropdown__text');
              var selectedOpt = sel.options[sel.selectedIndex];
              if (triggerText && selectedOpt) {
                triggerText.textContent = selectedOpt.textContent;
              }
              // Update is-selected class
              customDD.querySelectorAll('.custom-dropdown__item').forEach(function (li) {
                li.classList.remove('is-selected');
                if (parseInt(li.getAttribute('data-index'), 10) === sel.selectedIndex) {
                  li.classList.add('is-selected');
                }
              });
            }
          }
        } else {
          groupEl.classList.remove('is-not-available');
          groupEl.classList.remove('is-not-available--hidden');
          groupEl.classList.remove('is-not-available--disabled');
          sel.disabled = false;
        }
      });

      /* ---- requiredIf ---- */
      allGroups.forEach(function (groupEl) {
        // Skip groups that are currently hidden by notAvailableIf
        if (groupEl.classList.contains('is-not-available')) return;

        var optionName = groupEl.getAttribute('data-option-name');
        var configEntry = null;
        for (var ci = 0; ci < addonConfig.length; ci++) {
          if (addonConfig[ci].name === optionName) {
            configEntry = addonConfig[ci];
            break;
          }
        }

        var rules = configEntry && configEntry.requiredIf;
        if (!rules || !Array.isArray(rules) || rules.length === 0) return;

        var sel = groupEl.querySelector('[data-custom-option-select]');
        var indicator = groupEl.querySelector('[data-required-indicator]');
        if (!sel) return;

        var shouldBeRequired = false;
        var allowedVariantIds = []; // union of targetVariantIds from triggered rules
        var hasTargetFilter = false;

        rules.forEach(function (rule) {
          // Find the trigger group's select by matching data-option-name
          var triggerGroupName = rule.group;
          var triggerSel = wrapper.querySelector(
            '[data-custom-option-select][data-option-name="' + triggerGroupName + '"]'
          );
          if (!triggerSel) return;

          var triggerOpt = triggerSel.options[triggerSel.selectedIndex];
          if (!triggerOpt) return;
          var triggerVariantId = triggerOpt.getAttribute('data-variant-id');
          if (!triggerVariantId) return;

          // Compare as strings — Liquid may serialise numeric-looking IDs as integers
          if (rule.variantIds && rule.variantIds.map(String).indexOf(String(triggerVariantId)) !== -1) {
            shouldBeRequired = true;
            // Collect targetVariantIds from this triggered rule
            var targets = rule.targetVariantIds || [];
            if (targets.length > 0) {
              hasTargetFilter = true;
              targets.forEach(function (tid) {
                if (allowedVariantIds.indexOf(String(tid)) === -1) {
                  allowedVariantIds.push(String(tid));
                }
              });
            }
          }
        });

        // Filter dropdown options based on targetVariantIds from triggered rules
        var customDD = groupEl.querySelector('.custom-dropdown');
        for (var oi = 0; oi < sel.options.length; oi++) {
          var opt = sel.options[oi];
          var vid = opt.getAttribute('data-variant-id');
          // Always show the placeholder option (no variant-id)
          if (!vid || vid === '') continue;

          var shouldShow = !hasTargetFilter || allowedVariantIds.indexOf(String(vid)) !== -1;
          opt.hidden = !shouldShow;
          opt.disabled = !shouldShow;

          // Also update custom dropdown items
          if (customDD) {
            var li = customDD.querySelector('.custom-dropdown__item[data-index="' + oi + '"]');
            if (li) {
              li.style.display = shouldShow ? '' : 'none';
            }
          }
        }

        // If current selection is now hidden, reset to placeholder
        if (hasTargetFilter) {
          var currentOpt = sel.options[sel.selectedIndex];
          var currentVid = currentOpt ? currentOpt.getAttribute('data-variant-id') : '';
          if (currentVid && allowedVariantIds.indexOf(String(currentVid)) === -1) {
            sel.selectedIndex = 0;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            if (customDD) {
              var triggerText = customDD.querySelector('.custom-dropdown__text');
              if (triggerText && sel.options[0]) {
                triggerText.textContent = sel.options[0].textContent;
              }
              customDD.querySelectorAll('.custom-dropdown__item').forEach(function (li) {
                li.classList.remove('is-selected');
                if (parseInt(li.getAttribute('data-index'), 10) === 0) {
                  li.classList.add('is-selected');
                }
              });
            }
          }
        }

        // When no rule is triggered, show all options
        if (!shouldBeRequired) {
          for (var ri = 0; ri < sel.options.length; ri++) {
            sel.options[ri].hidden = false;
            sel.options[ri].disabled = false;
            if (customDD) {
              var rli = customDD.querySelector('.custom-dropdown__item[data-index="' + ri + '"]');
              if (rli) rli.style.display = '';
            }
          }
        }

        // Toggle the required state
        sel.setAttribute('data-option-required', shouldBeRequired ? 'true' : 'false');
        if (shouldBeRequired) {
          sel.setAttribute('required', '');
        } else {
          sel.removeAttribute('required');
        }
        if (indicator) {
          indicator.style.display = shouldBeRequired ? '' : 'none';
        }
        // Update the parent group data attribute too
        groupEl.setAttribute('data-option-required', shouldBeRequired ? 'true' : 'false');
      });
    }

    // Re-update when the theme fires a variant price change
    // Capture the new variant price directly from the event to avoid race conditions
    if (section && section.addEventListener) {
      section.addEventListener('variantPriceChange', function (evt) {
        if (evt.detail && evt.detail.variant && evt.detail.variant.price != null) {
          basePrice = evt.detail.variant.price;
        }
        // Use requestAnimationFrame to run after the theme updates the DOM
        requestAnimationFrame(function () {
          update();
        });
      });

      // Also listen for general variant changes (covers cases where price doesn't change
      // but variant does, e.g., same-priced variants)
      section.addEventListener('variantChange', function (evt) {
        if (evt.detail && evt.detail.variant && evt.detail.variant.price != null) {
          basePrice = evt.detail.variant.price;
          // Keep the data attribute in sync for reloads
          wrapper.setAttribute('data-current-variant-price', String(evt.detail.variant.price));
        }

        // Check if we need to reload addon config for this variant
        var currentVariantId = wrapper.getAttribute('data-current-variant-id');
        var newVariantId = evt.detail && evt.detail.variant ? String(evt.detail.variant.id) : '';

        // Only re-render for products that actually carry per-variant content
        // (addon config, includes, or specs). Liquid sets data-variant-aware; for
        // everything else — e.g. Trillium, with one product-level config shared by
        // all variants — we skip the reload and leave behavior exactly as before.
        var variantAware = wrapper.getAttribute('data-variant-aware') === 'true';
        if (variantAware && newVariantId && currentVariantId !== newVariantId) {
          // Update current variant ID
          wrapper.setAttribute('data-current-variant-id', newVariantId);

          // Re-render the section server-side for the new variant so its
          // variant-level product_addons_config (or the product-level fallback)
          // is applied, along with the [data-variant-swap] regions. We reload
          // rather than probing /products/{handle}.js for variant metafields —
          // the AJAX API does not expose variant metafields, so that probe cannot
          // tell when two variants carry *different* configs.
          reloadProductCustomOptions();
        }

        requestAnimationFrame(function () {
          update();
        });
      });
    }

    /**
     * Reload the custom options section for the current variant
     * This triggers a fresh Liquid render with variant-specific context
     */
    function reloadProductCustomOptions() {
      var productForm = document.getElementById(formId);
      if (!productForm) return;

      var sectionContainer = wrapper.closest('[data-section-id]');
      if (!sectionContainer) return;

      var sectionId = sectionContainer.getAttribute('data-section-id');
      if (!sectionId) return;

      var currentVariantId = wrapper.getAttribute('data-current-variant-id');
      if (!currentVariantId) return;

      // Build the section rendering URL with variant context
      var url = window.location.pathname + '?section_id=' + sectionId + '&variant=' + currentVariantId;

      fetch(url)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newCustomOptions = doc.querySelector('[data-custom-options]');

          if (newCustomOptions) {
            // Replace the current custom options with the new variant-specific version
            wrapper.parentNode.replaceChild(newCustomOptions, wrapper);

            // Re-initialize the new custom options section
            // Note: This will create a new instance with the fresh configuration
            if (window.ProductCustomOptions && window.ProductCustomOptions.init) {
              window.ProductCustomOptions.init();
            } else {
              // Fallback: dispatch a custom event that the main script can listen for
              document.dispatchEvent(new CustomEvent('customOptions:reload', {
                detail: { container: newCustomOptions }
              }));
            }
          }

          // Swap any other variant-dependent regions this section rendered
          // (e.g. the Includes list and the Specifications tab body), matched by
          // a shared data-variant-swap key. Fallback-safe: products whose theme
          // output has no tagged regions are unaffected.
          var liveSection = document.querySelector('[data-section-id="' + sectionId + '"]');
          if (liveSection) {
            doc.querySelectorAll('[data-variant-swap]').forEach(function (newRegion) {
              var key = newRegion.getAttribute('data-variant-swap');
              var liveRegion = liveSection.querySelector('[data-variant-swap="' + key + '"]');
              if (liveRegion) liveRegion.parentNode.replaceChild(newRegion, liveRegion);
            });
          }
        })
        .catch(function (err) {
          console.warn('[Custom Options] Error reloading section:', err);
        });
    }

    // Reset every select to the placeholder (index 0) so nothing is pre-selected.
    // Also sync each custom dropdown trigger text + is-selected class.
    selects.forEach(function (sel) {
      sel.selectedIndex = 0;
      var customDD = sel.parentNode.querySelector('.custom-dropdown');
      if (customDD) {
        var triggerText = customDD.querySelector('.custom-dropdown__text');
        var placeholderOpt = sel.options[0];
        if (triggerText && placeholderOpt) {
          triggerText.textContent = placeholderOpt.textContent;
        }
        customDD.querySelectorAll('.custom-dropdown__item').forEach(function (li) {
          li.classList.remove('is-selected');
          if (parseInt(li.getAttribute('data-index'), 10) === 0) {
            li.classList.add('is-selected');
          }
        });
      }
    });

    // Initial run
    updatingDeps = true;
    updateDependencies();
    updatingDeps = false;
    update();

    // Initialize prebuilt pricing display when the prefabricated breakdown is
    // visible — either because the tabs are present (and we default to "As
    // shown") or because every group has disableCustomization: true and the
    // breakdown is rendered on its own without tabs.
    if (hasTabsEnabled) {
      updatePrebuiltPricing();
      // Set prefabricated as default tab
      switchTab('prefabricated');
    } else if (prefabOnly) {
      updatePrebuiltPricing();
    }
  }

  // Initialize on page load
  ready(initCustomOptions);

  // Expose initialization function globally for reloads
  window.ProductCustomOptions = {
    init: initCustomOptions
  };

  // Listen for reload events (when variant changes require config refresh)
  document.addEventListener('customOptions:reload', function (evt) {
    var container = evt.detail && evt.detail.container;
    if (container) {
      container.removeAttribute('data-initialized');
      initCustomOptions(container);
    } else {
      initCustomOptions();
    }
  });
})();
